import { useEffect, useState } from 'react';

/** Tailwind `md` breakpoint — phones are below this width. */
export const MOBILE_MAX_WIDTH_PX = 767;

/** Tailwind `lg` breakpoint — tablets are md through lg-1. */
export const TABLET_MIN_WIDTH_PX = 768;
export const TABLET_MAX_WIDTH_PX = 1023;

function getMatch(query: string): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(query).matches;
}

/**
 * Subscribe to a CSS media query. SSR-safe (defaults to false until mounted).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => getMatch(query));

  useEffect(() => {
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [query]);

  return matches;
}

/** Viewport width < 768px (Tailwind `max-md`). */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`);
}

/** Viewport width 768px–1023px (tablet portrait / small landscape). */
export function useIsTablet(): boolean {
  return useMediaQuery(
    `(min-width: ${TABLET_MIN_WIDTH_PX}px) and (max-width: ${TABLET_MAX_WIDTH_PX}px)`,
  );
}

/** Viewport width >= 1024px (Tailwind `lg+`). */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${TABLET_MAX_WIDTH_PX + 1}px)`);
}
